import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';

const Reports = () => {
  const { token } = useAuth();
  const [threats, setThreats] = useState([]);
  const [violations, setViolations] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('security-summary');
  const [dateRange, setDateRange] = useState('7days');

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [threatsData, violationsData, devicesData] = await Promise.all([
        api.getThreats(token),
        api.getViolations(token),
        api.getDevices(token)
      ]);

      setThreats(Array.isArray(threatsData) ? threatsData : []);
      setViolations(Array.isArray(violationsData) ? violationsData : []);
      setDevices(Array.isArray(devicesData) ? devicesData : []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = () => {
    alert(`Generating ${reportType} report for ${dateRange}...`);
  };

  const handleExportPDF = () => {
    alert('Exporting report as PDF...');
  };

  const handleExportCSV = () => {
    alert('Exporting report as CSV...');
  };

  const stats = {
    totalThreats: threats.length,
    criticalThreats: threats.filter(t => t.severity === 'Critical').length,
    activeThreats: threats.filter(t => t.status === 'Active').length,
    totalViolations: violations.length,
    totalDevices: devices.length,
    onlineDevices: devices.filter(d => d.status === 'Online').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cyber-dark flex items-center justify-center">
        <div className="text-white text-xl">Loading reports...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cyber-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Reports & Analytics</h1>
          <p className="text-gray-400 mt-1">Generate security reports and view analytics</p>
        </div>

        <div className="bg-cyber-card border border-gray-800 rounded-lg p-6 mb-8">
          <h3 className="text-xl font-semibold text-white mb-4">Generate Report</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Report Type</label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="w-full px-4 py-2 bg-cyber-darker border border-gray-700 rounded-lg text-white focus:border-cyber-cyan focus:outline-none"
              >
                <option value="security-summary">Security Summary</option>
                <option value="threat-analysis">Threat Analysis</option>
                <option value="compliance">Compliance Report</option>
                <option value="access-control">Access Control Report</option>
                <option value="device-inventory">Device Inventory</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-400 mb-2 block">Date Range</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full px-4 py-2 bg-cyber-darker border border-gray-700 rounded-lg text-white focus:border-cyber-cyan focus:outline-none"
              >
                <option value="24hours">Last 24 Hours</option>
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
                <option value="90days">Last 90 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={handleGenerateReport}
                className="w-full px-6 py-2 bg-cyber-cyan text-cyber-darker font-semibold rounded-lg hover:bg-cyan-400 transition-colors"
              >
                Generate Report
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-6">
            <h3 className="text-sm font-medium text-gray-400 mb-2">Total Threats</h3>
            <div className="text-3xl font-bold text-white mb-1">{stats.totalThreats}</div>
            <p className="text-sm text-gray-400">{stats.criticalThreats} critical, {stats.activeThreats} active</p>
          </div>

          <div className="bg-cyber-card border border-gray-800 rounded-lg p-6">
            <h3 className="text-sm font-medium text-gray-400 mb-2">Policy Violations</h3>
            <div className="text-3xl font-bold text-white mb-1">{stats.totalViolations}</div>
            <p className="text-sm text-gray-400">Access control violations</p>
          </div>

          <div className="bg-cyber-card border border-gray-800 rounded-lg p-6">
            <h3 className="text-sm font-medium text-gray-400 mb-2">Devices Monitored</h3>
            <div className="text-3xl font-bold text-white mb-1">{stats.onlineDevices}/{stats.totalDevices}</div>
            <p className="text-sm text-gray-400">Online / Total devices</p>
          </div>
        </div>

        <div className="bg-cyber-card border border-gray-800 rounded-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-white">Generated Reports</h3>
            <div className="flex space-x-2">
              <button
                onClick={handleExportPDF}
                className="px-4 py-2 bg-red-500/20 text-red-400 text-sm font-semibold rounded border border-red-500/30 hover:bg-red-500/30 transition-colors"
              >
                Export PDF
              </button>
              <button
                onClick={handleExportCSV}
                className="px-4 py-2 bg-green-500/20 text-green-400 text-sm font-semibold rounded border border-green-500/30 hover:bg-green-500/30 transition-colors"
              >
                Export CSV
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Report Type</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Generated</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Date Range</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-700 hover:bg-cyber-darker transition-colors">
                  <td className="py-4 px-4 text-sm text-white">Security Summary</td>
                  <td className="py-4 px-4 text-sm text-gray-400">{new Date().toLocaleDateString()}</td>
                  <td className="py-4 px-4 text-sm text-gray-400">Last 7 Days</td>
                  <td className="py-4 px-4">
                    <span className="px-2 py-1 text-xs rounded bg-green-500/20 text-green-400 border border-green-500/30">
                      Complete
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <button className="text-cyber-cyan hover:text-cyan-400 text-sm font-medium">
                      Download
                    </button>
                  </td>
                </tr>
                <tr className="border-b border-gray-700 hover:bg-cyber-darker transition-colors">
                  <td className="py-4 px-4 text-sm text-white">Threat Analysis</td>
                  <td className="py-4 px-4 text-sm text-gray-400">{new Date(Date.now() - 86400000).toLocaleDateString()}</td>
                  <td className="py-4 px-4 text-sm text-gray-400">Last 30 Days</td>
                  <td className="py-4 px-4">
                    <span className="px-2 py-1 text-xs rounded bg-green-500/20 text-green-400 border border-green-500/30">
                      Complete
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <button className="text-cyber-cyan hover:text-cyan-400 text-sm font-medium">
                      Download
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-cyber-card border border-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-white mb-4">Threat Analysis</h3>
          <div className="space-y-4">
            {threats.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No threat data available</p>
            ) : (
              threats.slice(0, 5).map((threat) => (
                <div key={threat.id} className="bg-cyber-darker border border-gray-700 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="text-white font-medium">{threat.type}</h4>
                      <p className="text-sm text-gray-400">{threat.device_name || 'Unknown Device'}</p>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 text-xs rounded ${
                        threat.severity === 'Critical'
                          ? 'bg-red-500/20 text-red-400'
                          : threat.severity === 'High'
                          ? 'bg-orange-500/20 text-orange-400'
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {threat.severity}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">{new Date(threat.detected_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;